import { useCallback, useMemo } from "react";
import { useTrackedSessionStorage } from "@/hooks/use-tracked-browser-storage";

// Type definition for our sessionStorage object
interface CampaignStatusesMap {
  [campaignId: string]: string[];
}

/**
 * Hook for managing campaign status selection with tab-specific persistence.
 *
 * This hook provides functionality to:
 * 1. Select campaign statuses and persist the selection in sessionStorage
 * 2. Maintain tab-specific status selections (view preference)
 * 3. Handle campaign switching by maintaining separate selections per campaign
 */
type UpdateOptions = {
  intentionalClear?: boolean;
  resetToServerDefaults?: boolean;
};

export function useCampaignStatusSelection(campaignId?: string) {
  const [auto, setAuto] = useTrackedSessionStorage<boolean>(
    "campaigns::explorer::engagement-statuses::auto",
    true,
  );
  // Manage selected statuses with tab-specific persistence
  const [selectedStatusesMap, setSelectedStatusesMap] =
    useTrackedSessionStorage<CampaignStatusesMap>(
      "campaigns::explorer::engagement-statuses::filters",
      {},
    );

  // Get the selected statuses for this campaign
  const selectedStatuses = useMemo(() => {
    if (!campaignId) return [];
    return selectedStatusesMap[campaignId] || [];
  }, [campaignId, selectedStatusesMap]);

  // Update selected statuses
  const handleStatusesChange = useCallback(
    (statuses: string[], options?: UpdateOptions) => {
      if (!campaignId) return;

      // Create a new map to avoid mutating the existing one
      const newMap = { ...selectedStatusesMap };

      // Update logic:
      // - resetToServerDefaults: remove key so true defaults are used by queries
      // - intentionalClear with empty array: store empty array to represent explicit choice
      // - empty without flag: programmatic clear → remove key
      if (options?.resetToServerDefaults) {
        delete newMap[campaignId];
      } else if (statuses.length === 0) {
        if (options?.intentionalClear) {
          newMap[campaignId] = [];
        } else {
          delete newMap[campaignId];
        }
      } else {
        newMap[campaignId] = statuses;
      }

      setSelectedStatusesMap(newMap);
    },
    [campaignId, selectedStatusesMap, setSelectedStatusesMap],
  );

  const clearAllOverrides = useCallback(() => {
    setSelectedStatusesMap({});
  }, [setSelectedStatusesMap]);

  return {
    selectedStatuses,
    handleStatusesChange,
    auto,
    setAuto,
    clearAllOverrides,
    // Expose the map for debugging/testing
    selectedStatusesMap,
    hasOverride: !!campaignId && Object.hasOwn(selectedStatusesMap, campaignId),
  };
}
